import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import anime from 'animejs';

interface Scene3DProps {
  className?: string;
  enableParticles?: boolean;
  enableRotation?: boolean;
}

export function Scene3D({ 
  className = "", 
  enableParticles = true, 
  enableRotation = true 
}: Scene3DProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const frameId = useRef<number>();

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true,
      powerPreference: "high-performance"
    });
    
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mountRef.current.appendChild(renderer.domElement);

    sceneRef.current = scene;
    rendererRef.current = renderer;

    // Create floating geometric shapes
    const geometries = [
      new THREE.OctahedronGeometry(0.5),
      new THREE.TetrahedronGeometry(0.6),
      new THREE.IcosahedronGeometry(0.4),
      new THREE.DodecahedronGeometry(0.5)
    ];

    const materials = [
      new THREE.MeshBasicMaterial({ 
        color: 0xff006e, 
        wireframe: true,
        transparent: true,
        opacity: 0.6
      }),
      new THREE.MeshBasicMaterial({ 
        color: 0x8338ec, 
        wireframe: true,
        transparent: true,
        opacity: 0.6
      }),
      new THREE.MeshBasicMaterial({ 
        color: 0x3a86ff, 
        wireframe: true,
        transparent: true,
        opacity: 0.6
      }),
      new THREE.MeshBasicMaterial({ 
        color: 0x06ffa5, 
        wireframe: true,
        transparent: true,
        opacity: 0.6
      })
    ];

    const meshes: THREE.Mesh[] = [];

    // Create multiple floating objects
    for (let i = 0; i < 8; i++) {
      const geometry = geometries[i % geometries.length];
      const material = materials[i % materials.length];
      const mesh = new THREE.Mesh(geometry, material);
      
      // Random positioning
      mesh.position.set(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10
      );
      
      // Random rotation
      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      
      scene.add(mesh);
      meshes.push(mesh);
    }

    // Particle system
    let particles: THREE.Points | null = null;
    if (enableParticles) {
      const particleGeometry = new THREE.BufferGeometry();
      const particleCount = 1000;
      const positions = new Float32Array(particleCount * 3);
      
      for (let i = 0; i < particleCount * 3; i++) {
        positions[i] = (Math.random() - 0.5) * 20;
      }
      
      particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      const particleMaterial = new THREE.PointsMaterial({
        color: 0x06ffa5,
        size: 0.02,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending
      });
      
      particles = new THREE.Points(particleGeometry, particleMaterial);
      scene.add(particles);
    }

    // Camera positioning
    camera.position.z = 5;

    // Animation loop
    const animate = () => {
      frameId.current = requestAnimationFrame(animate);

      if (enableRotation) {
        // Rotate meshes
        meshes.forEach((mesh, index) => {
          mesh.rotation.x += 0.005 + index * 0.001;
          mesh.rotation.y += 0.005 + index * 0.001;
          
          // Floating animation
          mesh.position.y += Math.sin(Date.now() * 0.001 + index) * 0.002;
        });

        // Rotate particles
        if (particles) {
          particles.rotation.y += 0.001;
        }
      }

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current || !camera || !renderer) return;
      
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Animate entrance
    anime({
      targets: meshes.map(mesh => mesh.scale),
      x: [0, 1],
      y: [0, 1],
      z: [0, 1],
      duration: 2000,
      delay: (_el, i) => i * 200,
      easing: 'easeOutElastic(1, .8)'
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      // Cleanup
      meshes.forEach(mesh => {
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(material => material.dispose());
        } else {
          mesh.material.dispose();
        }
      });
      
      if (particles) {
        particles.geometry.dispose();
        if (Array.isArray(particles.material)) {
          particles.material.forEach(material => material.dispose());
        } else {
          particles.material.dispose();
        }
      }
      
      renderer.dispose();
    };
  }, [enableParticles, enableRotation]);

  return (
    <div 
      ref={mountRef} 
      className={`w-full h-full ${className}`}
      style={{ minHeight: '400px' }}
    />
  );
}